import { apiClient } from '@/core/api/client';
import {
  Message,
  ConversationsResponse,
  ConversationMessagesResponse,
  SendMessageDto,
  FirstContactEligibility,
} from '../types';
import { normalizeMessage } from '../utils/normalizeMessage';

/**
 * Send a message to another user
 * 
 * Note: WebSocket is preferred for active conversations.
 * This REST endpoint is fallback for when WebSocket is unavailable.
 * 
 * @param dto - Message data (recipientId, content)
 * @returns Created message
 */
export const sendMessage = async (dto: SendMessageDto): Promise<Message> => {
  const response = await apiClient.post<Message>('/messages', dto);
  return normalizeMessage(response.data as any);
};

/**
 * Get list of user conversations
 * 
 * Returns conversations ordered by most recent message.
 * Includes message preview, unread count, and participant info.
 * 
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 20, max: 50)
 * @returns Paginated list of conversations
 */
export const getConversations = async (
  page = 1,
  limit = 20
): Promise<ConversationsResponse> => {
  const response = await apiClient.get<ConversationsResponse>('/messages/conversations', {
    params: { page, limit },
  });
  return response.data;
};

/**
 * Get messages in a specific conversation
 * 
 * Automatically marks messages as read when retrieved.
 * Supports pagination for loading older messages.
 * 
 * @param conversationId - Conversation ID (format: user1_user2)
 * @param limit - Number of messages to load (default: 50, max: 100)
 * @param before - Load messages before this timestamp (for pagination)
 * @returns Conversation messages and metadata
 */
export const getConversationMessages = async (
  conversationId: string,
  limit = 50,
  before?: Date
): Promise<ConversationMessagesResponse> => {
  const params: Record<string, string | number> = { limit };
  if (before) {
    params.before = before.toISOString();
  }

  const response = await apiClient.get<ConversationMessagesResponse>(
    `/messages/conversations/${conversationId}`,
    { params }
  );
  
  const data = response.data;
  data.messages = data.messages.map((msg) => normalizeMessage(msg as any));
  return data;
};

/**
 * Get unread message count
 * 
 * Used for notification badge polling.
 * Poll interval: 30 seconds (FR-080 requirement).
 * 
 * @returns Total count of unread messages
 */
export const getUnreadCount = async (): Promise<number> => {
  const response = await apiClient.get<{ unreadCount: number }>('/messages/unread-count');
  return response.data.unreadCount;
};

/**
 * Side-effect-free, workspace-scoped read: does the active workspace owner
 * already have a persisted message with this SP? Guides the pre-chat
 * launch-gate only — the first-send transaction remains authoritative
 * (spec §3.2).
 *
 * @param serviceProviderId - service_providers.id to check eligibility for
 */
export const getFirstContactEligibility = async (
  serviceProviderId: string,
): Promise<FirstContactEligibility> => {
  const response = await apiClient.get<FirstContactEligibility>(
    '/messages/first-contact-eligibility',
    { params: { serviceProviderId } },
  );
  return response.data;
};

